import { useEffect, useRef } from 'react';

/**
 * The tab you are already on, tapped again: the way back to the top of it.
 *
 * Every tab has screens pushed inside it — a gathering, the table of
 * attendance, «Профиль» — and until this existed the client's own arrow was the
 * only way out of one. Tapping the tab did nothing at all: `setView` was handed
 * the view it already held, React re-rendered the same screen, and the pushed
 * page underneath was never told. A person who does not know the arrow is
 * Telegram's rather than ours taps the lit tab, which is what every other app
 * with a bar like this answers, and gets no answer.
 *
 * A counter and not a flag, because two taps in a row are two requests to go
 * home and a flag that is already true is one. The screens read it and reset
 * whatever they have pushed.
 *
 * `seen` rather than a mounted flag: under StrictMode the effect runs twice on
 * mount with the same number, and a flag would read the second run as a press.
 */
export function useHome(press: number, toRoot: () => void): void {
  // Read inside the effect, the way `useBackKey` reads its callback: the
  // callers write `() => setPage(...)` inline, so a fresh closure arrives on
  // every render and depending on it would fire this on renders that are not
  // presses.
  const go = useRef(toRoot);
  go.current = toRoot;

  const seen = useRef(press);
  useEffect(() => {
    if (seen.current === press) return;
    seen.current = press;
    go.current();
  }, [press]);
}
